import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useProgress } from "../context/ProgressContext";

const COLORS = ["#2F6F4F", "#D26040", "#1C2430", "#EFEBDE"];

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 60 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        duration: 2.2 + Math.random() * 1.8,
        size: 6 + Math.random() * 8,
        color: COLORS[i % COLORS.length],
        rotate: Math.random() * 360,
      })),
    []
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          initial={{ y: -40, opacity: 1, rotate: p.rotate }}
          animate={{ y: "110vh", rotate: p.rotate + 360, opacity: [1, 1, 0.9, 0.7] }}
          transition={{ duration: p.duration, delay: p.delay, ease: "easeIn", repeat: Infinity, repeatDelay: 0.4 }}
          style={{ left: `${p.left}%`, width: p.size, height: p.size * 1.4, background: p.color, position: "absolute", top: 0 }}
        />
      ))}
    </div>
  );
}

export function Celebration() {
  const { celebrating, dismissCelebration, stats, maxCombo } = useProgress();
  const { correct, accuracy, xp, rank } = stats;

  return (
    <AnimatePresence>
      {celebrating && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center px-5"
          data-testid="celebration"
        >
          <div className="absolute inset-0 bg-[#1C2430]/70 backdrop-blur-sm" onClick={dismissCelebration} />
          <Confetti />
          <motion.div
            initial={{ scale: 0.85, y: 40, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-[#FBFBF9] border-2 border-[#1C2430] max-w-lg w-full p-9 md:p-12 text-center"
          >
            <div className="font-mono-ui text-[10px] uppercase tracking-[0.26em] text-[#2F6F4F] mb-4">Guide complete</div>
            <h2 className="font-serif-display text-[44px] md:text-[56px] leading-[0.95] font-medium text-[#1C2430] mb-5">
              You finished
              <br />
              <span className="italic text-[#2F6F4F]">the whole thing.</span>
            </h2>
            <p className="text-[15px] leading-relaxed text-[#1C2430]/75 mb-8">
              All 15 practice questions done. That was week one for me: the reading. Now go do
              a mountain of real practice questions (AWS Skill Builder + the official practice exam) until
              the patterns are muscle memory. You've got this.
            </p>
            <div className="grid grid-cols-4 gap-3 mb-8">
              {[
                ["Rank", rank.name],
                ["XP", xp],
                ["Correct", `${correct}/15`],
                ["Accuracy", `${accuracy}%`],
              ].map(([label, val]) => (
                <div key={label} className="border border-[var(--border-heavy)] py-3">
                  <div className="font-serif-display text-[24px] text-[#1C2430] leading-none mb-1">{val}</div>
                  <div className="font-mono-ui text-[9px] uppercase tracking-[0.12em] text-[#1C2430]/50">{label}</div>
                </div>
              ))}
            </div>
            {maxCombo > 1 && (
              <p className="font-mono-ui text-[11px] uppercase tracking-[0.14em] text-[#D26040] mb-6">
                Best streak · {maxCombo}× in a row
              </p>
            )}
            <button
              onClick={dismissCelebration}
              className="font-mono-ui text-[11px] uppercase tracking-[0.18em] bg-[#1C2430] text-[#FBFBF9] px-6 py-3 hover:bg-[#364152] transition-colors"
              data-testid="celebration-dismiss"
            >
              Keep going ↑
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
